import { useCallback, useEffect, useMemo, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../store";
import "./ActionReportsTreeView.scss";
import { showProfile } from "../../../../reducers/profile";
import { LOS_GRID_EVENT } from "../../../Lit/acc-wc-views/src/acc-wc-view-los-indented/enum";
import TreeView from "../../../TreeView/TreeView";
import type { TreeViewParams, TreeNodeData } from "../../../TreeView/TreeViewDataLayer";
import { selectGroupLeader } from "../../../../reducers/groupLeaderFilter";
import { WhosNewMonthTabButtons } from "../../WhosNewActionReport/WhosNewMonthTabs";

const defaultParams: Partial<TreeViewParams> = {
  IncludeAboAttr: true,
  IncludeAwardLevels: true,
  IncludeFrontline: true,
  IncludeHasDownlineFlag: true,
  IncludeMap: true,
  IncludeSponStats: true,
  IncludeVolumeDetails: true,
};

const whosNewParams: Partial<TreeViewParams> = {
  ...defaultParams,
  IncludeInternational: true,
  DeduplicateInternational: true,
};

const renewalsParams: Partial<TreeViewParams> = {
  ...defaultParams,
  IncludeInactiveInGraceWindow: true,
  TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
};

type Props = {
  path?: "renewal" | "whosnew" | "whosclose" | "nbbv" | "cbr" | "cvr" | "iboOnboarding";
};

// Only populated for the whosnew "tree" child route, whose parent renders
// <Outlet context={{ treeTabSelected, onTreeTabSelect, ... }} /> — see
// WhosNewActionReportLayout.tsx. Undefined for every other report path.
type WhosNewOutletContext = {
  treeTabSelected?: number;
  onTreeTabSelect?: (months: number) => void;
};

const contextKeyMap: Record<NonNullable<Props["path"]>, string> = {
  renewal: "renewals",
  whosnew: "whos-new",
  whosclose: "whos-close",
  nbbv: "nbbv",
  cbr: "cbr",
  cvr: "cvr",
  iboOnboarding: "ibo-onboarding",
};

export function ActionReportsTreeView({ path = "whosnew" }: Props) {
  const { treeTabSelected, onTreeTabSelect } = useOutletContext<WhosNewOutletContext>() ?? {};
  const { aff, abo } = useAppSelector((state) => state.boot.user);
  const { isBootLoading } = useAppSelector((state) => state.boot);
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const { affAbosToExpand: whosNewAbos } = useAppSelector((state) => state.whosNewActionReportTracker);
  const { affAbosToExpand: renewalAbos } = useAppSelector((state) => state.renewalActionReportTracker);
  const selectedLeader = useAppSelector(selectGroupLeader);
  const { affAbosToExpand: whosCloseAbos } = useAppSelector((state) => state.whosCloseActionReportTracker);
  const { affAbosToExpand: nbbvAbos } = useAppSelector((state) => state.nbbvActionReportTracker);
  const { affAbosToExpand: cbrAbos } = useAppSelector((state) => state.cbrActionReportTracker);
  const { affAbosToExpand: cvrAbos } = useAppSelector((state) => state.cvrActionReportTracker);
  const { affAbosToExpand: iboOnboardingAbos } = useAppSelector((state) => state.iboOnboardingActionReportTracker);
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLDivElement>(null);

  const affAbosMap: Record<NonNullable<Props["path"]>, string[]> = {
    renewal: renewalAbos,
    whosnew: whosNewAbos,
    whosclose: whosCloseAbos,
    nbbv: nbbvAbos,
    cbr: cbrAbos,
    cvr: cvrAbos,
    iboOnboarding: iboOnboardingAbos,
  };

  const affAbosToExpand = affAbosMap[path];

  // When a group leader is selected, highlight only that leader's subtree
  // (the leader and any already-scheduled expand targets that fall under it).
  // Full subtree isolation in TreeView would require a data-layer refactor
  // (TreeView owns its own LOS Lite fetch); the highlight gives users a
  // visual anchor inside the full tree for this release.
  const highlightPredicate = useMemo((): ((node: TreeNodeData) => boolean) => {
    const expandSet = new Set(affAbosToExpand);
    if (selectedLeader) {
      const leaderKey = `${selectedLeader.aff.padStart(3, "0")}-${selectedLeader.abo}`;
      expandSet.add(leaderKey);
    }
    return (node) => expandSet.has(`${String(node.aff).padStart(3, "0")}-${node.abo}`);
  }, [affAbosToExpand, selectedLeader]);

  const showABOProfile = useCallback(
    (event: CustomEventInit) => {
      dispatch(showProfile({ affAbo: event.detail.abo.affAbo, isFromActionReport: true }));
    },
    [dispatch],
  );

  useEffect(() => {
    window.addEventListener(LOS_GRID_EVENT.ACTION_SEND_ABO, showABOProfile);
    return () => {
      window.removeEventListener(LOS_GRID_EVENT.ACTION_SEND_ABO, showABOProfile);
    };
  }, [showABOProfile]);

  if (isBootLoading || !aff || !abo || !selectedPeriod) {
    return (
      <div className="action-reports-tree-view">
        <div>Loading...</div>
      </div>
    );
  }

  const fullscreenExtraControls =
    path === "whosnew" && onTreeTabSelect ? (
      <WhosNewMonthTabButtons
        treeTabSelected={treeTabSelected ?? 1}
        onTabSelect={onTreeTabSelect}
        tabClassName="tree-view-fullscreen-header__controls-month-tab"
        activeTabClassName="tree-view-fullscreen-header__controls-month-tab--active"
      />
    ) : undefined;

  return (
    <div ref={containerRef} className="action-reports-tree-view" data-testid="action-reports-tree-view">
      <TreeView
        callData={true}
        period={selectedPeriod}
        params={path === "renewal" ? renewalsParams : path === "whosnew" ? whosNewParams : defaultParams}
        highlightPredicate={highlightPredicate}
        resetExpandedOnNewData={path !== "whosnew"}
        fullscreenExtraControls={fullscreenExtraControls}
        scrollRef={containerRef}
        contextKey={contextKeyMap[path]}
        enableFullscreen
        enableInternationalToggle={false}
      />
    </div>
  );
}
